import React, { useCallback, useState, useRef, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { verifyCode, resendCode } from "./auth";
import ResetForm from "./ResetForm";
import {
  codeToDigits,
  normalizeVerificationCode,
} from "../../utils/loginVerifyUtils";
import { clearDeepLinkVerificationCode } from "../../utils/deepLinkVerificationCode";

const CODE_LENGTH = 6;

function VerifyCode({ email, initialCode = "", setIsResetPass }) {
  const { t, i18n } = useTranslation("login");
  const [codeDigits, setCodeDigits] = useState(() => codeToDigits(initialCode));
  const [resendMessage, setResendMessage] = useState("");
  const [verifyError, setVerifyError] = useState("");
  const [isCodeVerified, setIsCodeVerified] = useState(false);
  const [resendError, setResendError] = useState("");
  const [isResending, setIsResending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const inputRefs = useRef([]);
  const hasAutoSubmittedRef = useRef(false);

  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    let timer;
    if (timerRunning && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prevTime) => prevTime - 1000);
      }, 1000);
    } else if (timeLeft === 0) {
      clearInterval(timer);
      setTimerRunning(false);
    }
    return () => clearInterval(timer);
  }, [timerRunning, timeLeft]);

  const handleDigitChange = (value, index) => {
    if (/^\d*$/.test(value)) {
      const newDigits = [...codeDigits];
      newDigits[index] = value;
      setCodeDigits(newDigits);
      setVerifyError("");

      // Move to next input if digit is entered
      if (value && index < codeDigits.length - 1) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  const handleKeyDown = (e, index) => {
    // Handle backspace/delete
    if (e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault();
      const newDigits = [...codeDigits];
      
      if (codeDigits[index]) {
        // If current field has value, clear it
        newDigits[index] = "";
        setCodeDigits(newDigits);
      } else if (index > 0) {
        // If current field is empty, move to previous field and clear it
        newDigits[index - 1] = "";
        setCodeDigits(newDigits);
        inputRefs.current[index - 1].focus();
      }
    }
    // Handle arrow keys for navigation
    else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      inputRefs.current[index - 1].focus();
    }
    else if (e.key === 'ArrowRight' && index < codeDigits.length - 1) {
      e.preventDefault();
      inputRefs.current[index + 1].focus();
    }
    // Handle paste
    else if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
      // Allow paste operation
      setTimeout(() => {
        const pastedValue = e.target.value;
        if (/^\d+$/.test(pastedValue)) {
          handlePaste(pastedValue, index);
        }
      }, 0);
    }
  };

  const handlePaste = (pastedText, startIndex) => {
    const digits = pastedText.replace(/\D/g, '').split('');
    const newDigits = [...codeDigits];
    
    for (let i = 0; i < digits.length && startIndex + i < codeDigits.length; i++) {
      newDigits[startIndex + i] = digits[i];
    }
    
    setCodeDigits(newDigits);
    
    // Focus on the next empty field or the last filled field
    const nextIndex = Math.min(startIndex + digits.length, codeDigits.length - 1);
    inputRefs.current[nextIndex].focus();
  };

  const submitVerification = useCallback(
    async (rawCode) => {
      const code = normalizeVerificationCode(rawCode);
      if (code.length !== CODE_LENGTH) {
        setVerifyError(t('VerifyCode.unexpectedError'));
        return;
      }
      if (isVerifying) {
        return;
      }

      setIsVerifying(true);
      setVerifyError("");
      setResendMessage("");

      try {
        await verifyCode(email, code);
        clearDeepLinkVerificationCode("reset", email);
        setIsCodeVerified(true);
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.response?.error ||
          error?.response?.message ||
          error?.message ||
          t('VerifyCode.unexpectedError');
        setVerifyError(errorMessage);
      } finally {
        setIsVerifying(false);
      }
    },
    [email, isVerifying, t],
  );

  const handleVerifyCodeSubmit = async (e) => {
    e.preventDefault();
    await submitVerification(codeDigits.join(""));
  };

  useEffect(() => {
    const normalized = normalizeVerificationCode(initialCode);
    if (normalized.length !== CODE_LENGTH) {
      return;
    }
    setCodeDigits(normalized.split(""));
  }, [initialCode]);

  useEffect(() => {
    const normalized = normalizeVerificationCode(initialCode);
    if (
      hasAutoSubmittedRef.current ||
      normalized.length !== CODE_LENGTH ||
      !email
    ) {
      return;
    }

    hasAutoSubmittedRef.current = true;
    submitVerification(normalized);
  }, [initialCode, email, submitVerification]);

  const handleResendCode = async () => {
    try {
      setVerifyError("");
      setResendError("");
      setResendMessage("");
      setIsResending(true);
      const response = await resendCode(email);
      if (response.status === 200 || response.status === 201) {
        setResendMessage(response.data.message);
        setTimerRunning(true);
        setTimeLeft(300000); // 5 minutes
      } else {
        setResendError(response.message);
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.response?.error ||
        error?.response?.message ||
        error?.message ||
        t('VerifyCode.unexpectedError');
      setResendError(errorMessage);
    } finally {
      setIsResending(false);
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes < 10 ? '0' : ''}${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  return (
    <>
      {isCodeVerified ? (
        <ResetForm email={email} setIsResetPass={setIsResetPass} />
      ) : (
        <>
          <h2 className="title-login">{t('VerifyCode.title')}</h2>
          <p className="subtitle-login">{t('VerifyCode.subtitle')}</p>
          <form onSubmit={handleVerifyCodeSubmit}>
            <div className="form-group-login">
              <div 
                className="digits-container"
                style={{
                  direction: 'ltr', // Always LTR for digits
                  display: 'flex',
                  gap: '8px',
                  justifyContent: 'center',
                  flexDirection: 'row' // Always left to right for digits
                }}
              >
                {codeDigits.map((digit, index) => (
                  <input
                    key={index}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleDigitChange(e.target.value, index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    onFocus={(e) => e.target.select()}
                    ref={(el) => (inputRefs.current[index] = el)}
                    className="digit-input"
                    style={{
                      textAlign: 'center',
                      direction: 'ltr', // Force LTR for individual inputs
                      unicodeBidi: 'bidi-override'
                    }}
                    autoComplete="one-time-code"
                  />
                ))}
              </div>
              {verifyError && <p className="login-error">{verifyError}</p>}
            </div>
            <div className="form-group-login">
              <button
                className="login-btn"
                type="submit"
                disabled={isVerifying || codeDigits.some((d) => d === "")}
              >
                {t('VerifyCode.submit')}
              </button>
            </div>
            {resendMessage && <p className="login-success">{resendMessage}</p>}
            {resendError && <p className="login-error">{resendError}</p>}
          </form>
          <div className="form-group-login">
            {timerRunning ? (
              <p className="text-white">
                {t('VerifyCode.resendIn', { time: formatTime(timeLeft / 1000) })}
              </p>
            ) : (
              <button
                className="resend-btn"
                type="button"
                onClick={handleResendCode}
                disabled={isResending}
              >
                {isResending ? t('VerifyCode.resending') : t('VerifyCode.resend')}
              </button>
            )}
          </div>
        </>
      )}
    </>
  );
}

export default VerifyCode;